import {useState} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {Bot, Send} from "lucide-react";
import {useCapabilities} from "../../../shared/auth/CapabilitiesContext";
import {i18n, useTranslation} from "../../../shared/i18n";
import {ADMIN_ROLES} from "../../../shared/auth/roles";
import {AdminTable} from "../../../shared/components/AdminTable";
import {MutationResultBanner} from "../../../shared/components/MutationResultBanner";
import {StatusBadge} from "../../../shared/components/StatusBadge";
import {asList, formatDateTime} from "../../../shared/utils/format";
import {listBotInvites, mapPartnerBotApiError, partnerBotQueryKeys, resolveBotCompany} from "../partnerBotApi";
import {PartnerBotInviteDialog} from "./PartnerBotInviteDialog";
import {PartnerBotRevokeDialog} from "./PartnerBotRevokeDialog";

const roleLabel = (role) => {
    const key = `orgs.partnerBot.roles.${role}`;
    const translated = i18n.t(key);
    return translated === key ? role : translated;
};

// Роль owner в инвайте разрешена только платформенным ролям актора.
const OWNER_INVITER_ROLES = new Set([
    ADMIN_ROLES.SUPERADMIN,
    ADMIN_ROLES.ADMINISTRATOR,
]);

// Не дёргаем ретраи на «ожидаемых» ответах: 403 — вне скоупа, 404 — нет компании.
const retryUnlessExpected = (failureCount, error) => {
    const status = error?.response?.status;
    if (status === 403 || status === 404) return false;
    return failureCount < 2;
};

export const getInviteStatus = (invite, now = new Date()) => {
    if (invite?.revoked_at) return {key: "revoked", label: i18n.t("orgs.partnerBot.status.revoked"), tone: "danger"};
    if (invite?.used_at || invite?.used_by_telegram_id) return {key: "used", label: i18n.t("orgs.partnerBot.status.used"), tone: "neutral"};
    const expiresAt = invite?.expires_at ? new Date(invite.expires_at) : null;
    const isExpired = Boolean(expiresAt) && !Number.isNaN(expiresAt.getTime()) && expiresAt.getTime() < now.getTime();
    if (invite?.is_active && !isExpired) return {key: "active", label: i18n.t("orgs.partnerBot.status.active"), tone: "success"};
    return {key: "expired", label: i18n.t("orgs.partnerBot.status.expired"), tone: "warning"};
};

export const PartnerBotSection = ({placeId, entity}) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const capabilities = useCapabilities();
    const [dialog, setDialog] = useState(null);
    const [revokingInvite, setRevokingInvite] = useState(null);
    const [mutationResult, setMutationResult] = useState(null);
    const canView = capabilities.hasAction("partner_bot.view");
    const canManage = capabilities.hasAction("partner_bot.manage");
    const canInviteOwner = OWNER_INVITER_ROLES.has(capabilities.user?.role);

    // Карточка живет на legacy place id — компанию для инвайтов резолвим отдельно.
    const companyQuery = useQuery({
        queryKey: partnerBotQueryKeys.company(placeId),
        queryFn: () => resolveBotCompany(placeId),
        enabled: canView && Boolean(placeId),
        retry: retryUnlessExpected,
    });
    const companyId = companyQuery.data?.company_id;

    const invitesQuery = useQuery({
        queryKey: partnerBotQueryKeys.invites(companyId),
        queryFn: () => listBotInvites(companyId),
        enabled: canView && companyId !== undefined && companyId !== null,
        retry: retryUnlessExpected,
    });

    if (!canView) return null;

    const invalidateInvites = () => {
        void queryClient.invalidateQueries({queryKey: partnerBotQueryKeys.invites(companyId)});
    };

    const closeDialogs = () => {
        setDialog(null);
        setRevokingInvite(null);
    };

    const openInviteDialog = () => {
        setMutationResult(null);
        setDialog("invite");
    };

    const openRevokeDialog = (invite) => {
        setMutationResult(null);
        setRevokingInvite(invite);
        setDialog("revoke");
    };

    const handleRevoked = () => {
        invalidateInvites();
        closeDialogs();
        setMutationResult({kind: "success", message: t("orgs.partnerBot.revoked")});
    };

    const hasCompany = companyQuery.isSuccess;
    const companyMissing = companyQuery.isError && companyQuery.error?.response?.status === 404;
    const companyError = companyQuery.isError && !companyMissing ? mapPartnerBotApiError(companyQuery.error) : null;
    const invites = asList(invitesQuery.data);
    const invitesError = invitesQuery.isError ? mapPartnerBotApiError(invitesQuery.error) : null;

    const columns = [
        {
            key: "role",
            label: t("orgs.common.role"),
            render: (invite) => (invite.role ? roleLabel(invite.role) : null) || invite.role || "—",
        },
        {
            key: "status",
            label: t("orgs.common.status"),
            render: (invite) => {
                const status = getInviteStatus(invite);
                return (
                    <span className="partner-bot-status-cell">
                        <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
                        {status.key === "used" && invite.used_by_telegram_id
                            ? <small className="admin-card-note">TG: {invite.used_by_telegram_id}</small>
                            : null}
                    </span>
                );
            },
        },
        {
            key: "created_at",
            label: t("orgs.partnerBot.created"),
            render: (invite) => formatDateTime(invite.created_at),
        },
        {
            key: "expires_at",
            label: t("orgs.partnerBot.expiresAt"),
            render: (invite) => formatDateTime(invite.expires_at),
        },
        ...(canManage ? [{
            key: "actions",
            label: t("orgs.common.actions"),
            render: (invite) => (
                getInviteStatus(invite).key === "active" ? (
                    <button
                        type="button"
                        className="admin-button admin-button-secondary"
                        onClick={() => openRevokeDialog(invite)}
                    >
                        {t("orgs.common.revoke")}
                    </button>
                ) : null
            ),
        }] : []),
    ];

    return (
        <article className="admin-card">
            <div className="partner-bot-section-header">
                <div>
                    <p className="admin-card-title"><Bot size={14} /> {t("orgs.partnerBot.title")}</p>
                    <p className="admin-card-note">
                        {t("orgs.partnerBot.copy")}
                    </p>
                </div>
                {canManage && hasCompany ? (
                    <button type="button" className="admin-button admin-button-primary" onClick={openInviteDialog}>
                        <Send aria-hidden="true" size={15} /> {t("orgs.partnerBot.invite")}
                    </button>
                ) : null}
            </div>

            <MutationResultBanner result={mutationResult} />

            {companyQuery.isLoading ? (
                <p className="admin-card-note">{t("orgs.partnerBot.resolving")}</p>
            ) : companyMissing ? (
                <p className="admin-card-note">{t("orgs.partnerBot.noCompany")}</p>
            ) : companyError ? (
                <p className="admin-card-note" role="alert">{companyError.message}</p>
            ) : hasCompany ? (
                <>
                    <p className="admin-card-note">{t("orgs.common.company", {name: companyQuery.data?.name || `#${companyId}`})}</p>
                    {invitesError ? (
                        <p className="admin-card-note" role="alert">{invitesError.message}</p>
                    ) : (
                        <AdminTable
                            columns={columns}
                            rows={invites}
                            loading={invitesQuery.isLoading}
                            emptyText={t("orgs.partnerBot.empty")}
                        />
                    )}
                </>
            ) : null}

            <PartnerBotInviteDialog
                open={dialog === "invite"}
                companyId={companyId}
                entity={entity}
                canInviteOwner={canInviteOwner}
                onCancel={closeDialogs}
                onCreated={invalidateInvites}
            />
            <PartnerBotRevokeDialog
                open={dialog === "revoke" && Boolean(revokingInvite)}
                invite={revokingInvite}
                entity={entity}
                onCancel={closeDialogs}
                onRevoked={handleRevoked}
            />
        </article>
    );
};
